import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';
import { useEffect, useState } from 'react';

export interface LeaderboardRow {
  user_id: string;
  display_name: string;
  calls: number;
  meaningful_pct: number;
  personal_emails: number;
  new_contacts: number;
  new_connections: number;
}

interface UseTeamLeaderboardArgs {
  workspaceId: string;
  from: Date;
  to: Date;
  tz: string;
  sortBy?: 'calls' | 'meaningful_pct' | 'personal_emails' | 'new_contacts' | 'new_connections';
  limit?: number;
  /** Pass the current user's role; query is enabled only when admin/owner. */
  role: string;
  enabled?: boolean;
}

export function useTeamLeaderboard(opts: UseTeamLeaderboardArgs): {
  rows: LeaderboardRow[];
  isLoading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
  reset: () => void;
} {
  const queryClient = useQueryClient();
  const isAdmin = opts.role === 'owner' || opts.role === 'admin';
  const [offset, setOffset] = useState(0);
  const [accumulated, setAccumulated] = useState<LeaderboardRow[]>([]);
  const [hasMore, setHasMore] = useState(false);

  const fromIso = opts.from.toISOString();
  const toIso = opts.to.toISOString();
  const sortBy = opts.sortBy ?? 'calls';
  const limit = opts.limit ?? 10;

  // Reset when key inputs change
  useEffect(() => {
    setOffset(0);
    setAccumulated([]);
    setHasMore(false);
  }, [opts.workspaceId, fromIso, toIso, sortBy, opts.role]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      ...queryKeys.analytics.leaderboard(opts.workspaceId, fromIso, toIso, sortBy, opts.role),
      offset,
    ],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_team_leaderboard', {
        p_workspace_id: opts.workspaceId,
        p_from: fromIso,
        p_to: toIso,
        p_tz: opts.tz,
        p_sort_by: sortBy,
        p_limit: limit + 1, // +1 to detect more pages
        p_offset: offset,
      });
      if (error) throw error;
      return (data ?? []) as LeaderboardRow[];
    },
    enabled: isAdmin && (opts.enabled ?? true),
    staleTime: 60_000,
  });

  // Merge new page into accumulated when data arrives
  useEffect(() => {
    if (!data) return;
    const more = data.length > limit;
    setHasMore(more);
    const page = data.slice(0, limit);
    if (offset === 0) {
      setAccumulated(page);
    } else {
      setAccumulated((prev) => {
        const seen = new Set(prev.map((r) => r.user_id));
        return [...prev, ...page.filter((r) => !seen.has(r.user_id))];
      });
    }
  }, [data, offset, limit]);

  return {
    rows: accumulated,
    isLoading,
    error: (error as Error) ?? null,
    hasMore,
    loadMore: () => {
      if (isLoading || !hasMore) return;
      setOffset((o) => o + limit);
    },
    reset: () => {
      setOffset(0);
      setAccumulated([]);
      setHasMore(false);
      queryClient.invalidateQueries({
        queryKey: queryKeys.analytics.leaderboard(opts.workspaceId, fromIso, toIso, sortBy, opts.role),
      });
    },
  };
}
